import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { goalId as selectGoalId } from '../FinancialPlanGoalDropdown/FinancialPlanGoalDropdownSelector';
import {
  invalidateFinancialPlanQuery,
  invalidateLongevityAnalysis,
  invalidateMonteCarloQuery,
  invalidateMarketScenariosQuery,
} from '../FinancialPlanOPL/Goals/helpers/legacyWhatIfsFinancialPlanHelpers';
import { WhatIf } from '../FinancialPlanOPL/Goals/whatIfs/components/WhatIf/WhatIf';
import { theme as defaultTheme, ThemeProvider } from '../theme/theme';
import { fetchGoalWhatIfData, setCurrentViewId } from './WhatIfsActions';
import {
  ANNUITY_PURCHASE,
  ESTATE_PLANNING_CHARITABLE_DONATIONS,
  ESTATE_PLANNING_LIFE_EXPECTANCIES,
  ESTATE_PLANNING_TAX_RATES,
  PROFILE_CHANGES_BUSINESS_SALE,
  PROFILE_CHANGES_INCOME_CHANGE,
  PROFILE_CHANGES_MISC_ASSET_SALE,
  PROFILE_CHANGES_PENSION_CHANGE,
  PROFILE_CHANGES_LIVING_EXPENSES_CHANGE,
  PROFILE_CHANGES_CHANGE_STATE,
  PROFILE_CHANGES_REAL_ESTATE_SALE,
  WINDFALL,
} from './WhatIfsConstants';
import {
  loading as selectIsLoadingWhatIfData,
  nullableFpIdSelector,
  nullableUrlSelector,
  whatIfOrDefaultSelector,
} from './WhatIfsSelector';
import { WhatIfTypeKey } from '../FinancialPlanOPL/Goals/whatIfs/WhatIfs.meta';
import { LoadingWhatIf } from '../FinancialPlanOPL/Goals/whatIfs/components/WhatIf/WhatIf.loading';

export const theme = {
  ...defaultTheme,
  colors: {
    ...defaultTheme.colors,
    bg: '#26282b',
    tooltipBg: defaultTheme.colors.gray['700'],
    primary: {
      50: '#052452',
      100: '#093970',
      200: '#104C8B',
      300: '#1960A6',
      400: '#0078D4',
      500: '#009FF9',
      600: '#7EC4F6',
      700: '#CCE7FF',
      800: '#E6F5FF',
      900: '#F3FBFF',
    },
    neutral: {
      50: '#1d1e20',
      100: '#26282b',
      200: '#383c42',
      300: '#494f5a',
      400: '#6c727f',
      500: '#9fa5b2',
      600: '#d1d4dc',
      700: '#e5e7eb',
      800: '#f4f5f7',
      900: '#f9fafb',
    },
    error: {
      50: '#540032',
      100: '#7E0042',
      200: '#A60048',
      300: '#C7094D',
      400: '#E01E5A',
      500: '#EF4268',
      600: '#F97386',
      700: '#FEAEB6',
      800: '#FFCED0',
      900: '#FFEDED',
    },
  },
};

const WHAT_IFS_WITH_OPEN_SIDEBAR = [
  WhatIfTypeKey.EstatePlanningCharitableDonations,
  WhatIfTypeKey.AnnuityPurchase,
];

const newWhatIfsBridge = (whatIfAreaId: string) => ({ clientId }) => {
  const dispatch = useDispatch();

  const goalId = useSelector(selectGoalId);
  const fpId = useSelector(nullableFpIdSelector);
  const whatIf = useSelector(whatIfOrDefaultSelector);
  const isLoadingWhatIfData = useSelector(selectIsLoadingWhatIfData);

  const goalPath = useSelector(nullableUrlSelector);

  const onSave = () => {
    dispatch(fetchGoalWhatIfData(clientId, fpId, goalId, goalPath));

    ADV.NewFinancialPlan.refreshPlan(true);

    invalidateFinancialPlanQuery({ clientId, fpId });

    invalidateMonteCarloQuery({ clientId, fpId });
    invalidateMarketScenariosQuery({ clientId, fpId });

    invalidateLongevityAnalysis({ clientId, fpId });

    // Keep the what-if sidebar open
    if (!WHAT_IFS_WITH_OPEN_SIDEBAR.includes(whatIf.WhatIf.WhatIfKey)) {
      dispatch(setCurrentViewId(clientId, goalId, null));
    }
  };

  if (isLoadingWhatIfData) {
    return <LoadingWhatIf />;
  }

  return (
    <ThemeProvider theme={theme}>
      <WhatIf
        {...whatIf}
        isRetrofit
        clientId={clientId}
        fpId={fpId}
        whatIfAreaId={whatIfAreaId}
        goalId={goalId}
        onSuccess={onSave}
        onDelete={onSave}
        onBack={() => {
          dispatch(setCurrentViewId(clientId, goalId, null));
        }}
      />
    </ThemeProvider>
  );
};

export const BusinessSale = newWhatIfsBridge(PROFILE_CHANGES_BUSINESS_SALE.toString());
export const RealEstateSale = newWhatIfsBridge(PROFILE_CHANGES_REAL_ESTATE_SALE.toString());
export const MiscAssetSale = newWhatIfsBridge(PROFILE_CHANGES_MISC_ASSET_SALE.toString());
export const IncomeChange = newWhatIfsBridge(PROFILE_CHANGES_INCOME_CHANGE.toString());
export const EstateTaxesChange = newWhatIfsBridge(ESTATE_PLANNING_TAX_RATES.toString());
export const EstatePlanningLifeExpectancies = newWhatIfsBridge(
  ESTATE_PLANNING_LIFE_EXPECTANCIES.toString(),
);
export const EstatePlanningCharitableDonations = newWhatIfsBridge(
  ESTATE_PLANNING_CHARITABLE_DONATIONS.toString(),
);
export const AnnuityPurchase = newWhatIfsBridge(ANNUITY_PURCHASE.toString());

export const Windfall = newWhatIfsBridge(WINDFALL.toString());

export const PensionChange = newWhatIfsBridge(PROFILE_CHANGES_PENSION_CHANGE.toString());

export const LivingExpensesChange = newWhatIfsBridge(
  PROFILE_CHANGES_LIVING_EXPENSES_CHANGE.toString(),
);

export const ChangeState = newWhatIfsBridge(PROFILE_CHANGES_CHANGE_STATE.toString());
